import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, saveSession, errMsg } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import LangToggle from '@/components/LangToggle';

export default function Register() {
  const nav = useNavigate();
  const { t } = useI18n();
  const [mode, setMode] = useState('email');
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.firstName || !form.lastName || !form.password) return toast.error(t('common.fillAll'));
    if (mode === 'email' && !form.email) return toast.error(t('register.emailReq'));
    if (mode === 'phone' && !form.phone) return toast.error(t('register.phoneReq'));
    if (form.password.length < 10) return toast.error(t('register.pwShort'));
    if (new TextEncoder().encode(form.password).length > 72) return toast.error(t('register.pwLong'));
    setLoading(true);
    try {
      const body = { first_name: form.firstName, last_name: form.lastName, password: form.password };
      if (mode === 'email') body.email = form.email; else body.phone = form.phone;
      const res = await api.post('/auth/register', body);
      saveSession(res.data.token, res.data.user);
      nav('/onboarding', { replace: true });
    } catch (err) {
      toast.error(errMsg(err, t('register.tryAgain')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-asgym-bg">
      <div className="mx-auto w-full max-w-[440px] px-6 py-10">
        <div className="flex items-center justify-between">
          <button onClick={() => nav('/')} data-testid="back-btn" className="flex items-center gap-1.5 text-sm font-bold text-asgym-accent">
            <ArrowLeft size={16} /> {t('common.back').toUpperCase()}
          </button>
          <LangToggle />
        </div>

        <h1 className="mt-8 font-display text-4xl font-extrabold leading-tight tracking-tight text-white">{t('register.title')}</h1>
        <p className="mb-8 mt-2 text-sm tracking-[2px] text-asgym-sub">{t('register.sub')}</p>

        <form onSubmit={submit} className="space-y-4">
          <div className="flex overflow-hidden rounded-xl border border-white/[0.06]">
            {['email', 'phone'].map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`reg-mode-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 py-3.5 text-[11px] font-bold tracking-[1.5px] transition-colors ${
                  mode === m ? 'bg-asgym-accent text-white' : 'bg-asgym-surface text-asgym-sub'
                }`}
              >
                {m === 'email' ? t('login.tabEmail') : t('login.tabPhone')}
              </button>
            ))}
          </div>

          <input data-testid="reg-first-name" className="as-input" placeholder={t('register.firstName')} value={form.firstName} onChange={set('firstName')} />
          <input data-testid="reg-last-name" className="as-input" placeholder={t('register.lastName')} value={form.lastName} onChange={set('lastName')} />
          {mode === 'email' ? (
            <input data-testid="reg-email" className="as-input" type="email" autoCapitalize="none" placeholder={t('register.email')} value={form.email} onChange={set('email')} />
          ) : (
            <input data-testid="reg-phone" className="as-input" type="tel" placeholder={t('register.phone')} value={form.phone} onChange={set('phone')} />
          )}
          <div className="flex items-center rounded-xl border border-white/[0.06] bg-black/30 focus-within:border-white/20">
            <input
              data-testid="reg-password"
              className="h-14 flex-1 bg-transparent px-4 text-[15px] text-white placeholder:text-asgym-muted outline-none"
              type={showPw ? 'text' : 'password'}
              placeholder={t('register.password')}
              value={form.password}
              onChange={set('password')}
            />
            <button type="button" onClick={() => setShowPw((s) => !s)} className="px-4 text-asgym-sub">
              {showPw ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <button type="submit" data-testid="reg-submit" disabled={loading} className="as-btn-primary w-full">
            {loading ? <Loader2 className="animate-spin" size={20} /> : t('register.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}
